import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, downloadExport, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const Stat = ({ label, value, testId }) => (<div className="kpi" data-testid={testId}><span className="eyebrow">{label}</span><span className="kpi-value">{value ?? 0}</span></div>);

export default function SearchDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/searches/${id}`).then((r) => alive && setS(r.data)).catch((e) => toast.error(errMsg(e)));
    load();
    const t = setInterval(() => { if (!s || ["Queued", "Running"].includes(s.status)) load(); }, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [id, s?.status]); // eslint-disable-line
  if (!s) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const st = s.stats || {};
  const running = ["Queued", "Running"].includes(s.status);
  const exportSearch = async () => {
    setBusy(true);
    try { const r = await api.post("/exports", { scope: "search", search_id: id }); await downloadExport(r.data.id, r.data.filename); toast.success("Excel downloaded"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="search-detail-page">
      <PageHeader title={s.name} sub={`${s.mode === "demo" ? "DEMO MODE (sample data)" : `Live · ${s.provider}`} · started ${fmtDate(s.started_at || s.created_at)}`}
        right={<>
          {running && <Button size="sm" variant="outline" onClick={() => api.post(`/searches/${id}/cancel`).then(() => toast("Cancel requested"))} data-testid="cancel-search-btn">Cancel</Button>}
          <Button size="sm" variant="outline" asChild><Link to={`/prospects?search_id=${id}`} data-testid="view-prospects-btn">View prospects</Link></Button>
          <Button size="sm" onClick={exportSearch} disabled={busy || running} data-testid="export-search-btn">{busy ? "Generating…" : "Download Excel"}</Button>
        </>} />
      {s.mode === "demo" && <div data-testid="demo-mode-banner" className="mb-4 text-xs border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm px-3 py-2"><b>Demo Mode:</b> these are labeled sample records, not live public web results. Add a Serper/Brave key in Settings to run live searches.</div>}
      <div className="panel p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium" data-testid="search-status">Status: <span className={running ? "text-amber-600 dark:text-amber-400" : s.status === "Completed" ? "text-emerald-600 dark:text-emerald-400" : ""}>{s.status}</span></span>
          <span className="text-xs text-muted-foreground font-mono">{s.progress ?? 0}% {s.duration_seconds ? `· ${s.duration_seconds}s` : ""}</span>
        </div>
        <Progress value={s.progress ?? 0} className="h-1.5" />
        {s.current_query && <p className="text-xs text-muted-foreground mt-2 font-mono truncate">Current query: {s.current_query}</p>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Stat label="Results examined" value={st.results_examined} testId="stat-examined" /><Stat label="Prospects found" value={st.prospects_found} testId="stat-found" />
        <Stat label="Duplicates removed" value={st.duplicates_removed} testId="stat-duplicates" /><Stat label="Hot / Warm" value={st.high_quality} testId="stat-hq" />
        <Stat label="Pages fetched" value={st.pages_fetched} /><Stat label="Blocked / skipped pages" value={st.pages_blocked} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-4">
          <p className="eyebrow mb-2">Queries run ({s.queries?.length || 0})</p>
          <ul className="space-y-1 font-mono text-[11px] text-muted-foreground max-h-64 overflow-auto" data-testid="queries-list">{(s.queries || []).map((q, i) => <li key={i}>{q}</li>)}</ul>
        </div>
        <div className="panel p-4">
          <p className="eyebrow mb-2">Source failures ({st.source_failures?.length || 0})</p>
          {(!st.source_failures || st.source_failures.length === 0) ? <p className="text-sm text-muted-foreground">None — every selected source responded.</p> :
            <ul className="space-y-1.5 text-xs" data-testid="source-failures">{st.source_failures.map((f, i) => <li key={i} className="border-l-2 border-destructive pl-2"><b>{f.source}</b>: {f.error}{f.query && <div className="font-mono text-muted-foreground truncate">{f.query}</div>}</li>)}</ul>}
          <p className="eyebrow mt-4 mb-2">Sources searched</p>
          <div className="flex flex-wrap gap-1">{(s.sources || []).map((x) => <span key={x} className="chip off cursor-default">{x.replace(/_/g, " ")}</span>)}</div>
        </div>
      </div>
    </div>
  );
}
